import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Spinner } from 'react-bootstrap';
import { FaEnvelope, FaFilePdf, FaWhatsapp } from 'react-icons/fa';
import { Search, ArrowClockwise } from 'react-bootstrap-icons';
import DashboardLayout from '../../components/DashboardLayout';
import { API_BASE_URL, apiRequest } from '../../utils/api';
import { formatEmployeeId } from '../../utils/employeeId';
import useOutletSearch from '../../hooks/useOutletSearch';
import { getActiveCompanyId } from '../../utils/companyContext';
import { getModulePermissions } from '../../utils/permissions';
import '../../assets/styles/files.css';
import '../../assets/styles/employees.css';

const MONTH_OPTIONS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
].map((label, index) => ({ value: String(index + 1), label }));

const formatDate = (value) => {
  if (!value) {
    return '-';
  }

  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-');
    return `${day}-${month}-${year}`;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString('en-GB');
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return value;
  }

  return numericValue.toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  });
};

const formatStatus = (value) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  if (value === 1 || value === '1') {
    return 'Active';
  }

  if (value === 0 || value === '0') {
    return 'Inactive';
  }

  return String(value);
};

const PaySlipList = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    search: '',
    month: '',
    year: '',
    branchId: '',
    employeeId: '',
  });
  const [payslips, setPayslips] = useState([]);
  const [meta, setMeta] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [employees, setEmployees] = useState([]);
  const [sharingId, setSharingId] = useState(null);
  const [emailSendingIds, setEmailSendingIds] = useState([]);
  const [actionStatus, setActionStatus] = useState(null);
  const [activeCompanyId, setActiveCompanyId] = useState(() => getActiveCompanyId());
  const payslipViewPermissions = getModulePermissions('payslip.view');
  const payslipSendPermissions = getModulePermissions('payslip.send');
  const canViewPayslipPdf = payslipViewPermissions.view;
  const canExportPayslipPdf = payslipViewPermissions.export;
  const canSendPayslipEmail = payslipSendPermissions.create;
  const canSharePayslipWhatsapp = payslipSendPermissions.export;
  const {
    searchTerm: outletSearchTerm,
    setSearchTerm: setOutletSearchTerm,
    options: outletOptions,
    loading: isOutletSearching,
    error: outletSearchError,
    search: searchOutlets,
    clearOptions: clearOutletOptions,
  } = useOutletSearch({ navigate, companyId: activeCompanyId, enabled: Boolean(activeCompanyId) });

  const monthOptions = useMemo(() => MONTH_OPTIONS, []);

  useEffect(() => {
    const handleActiveCompanyChanged = (event) => {
      const nextCompanyId = event?.detail?.id ?? getActiveCompanyId();
      setActiveCompanyId(nextCompanyId);
      setFilters((prev) => ({ ...prev, branchId: '' }));
      setOutletSearchTerm('');
      clearOutletOptions();
      setPage(1);
    };

    window.addEventListener('activeCompanyChanged', handleActiveCompanyChanged);
    return () => {
      window.removeEventListener('activeCompanyChanged', handleActiveCompanyChanged);
    };
  }, [clearOutletOptions, setOutletSearchTerm]);


  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({ search: '', month: '', year: '', branchId: '', employeeId: '' });
    setPage(1);
    setOutletSearchTerm('');
    clearOutletOptions();
  };

  const resolveEmployeePhone = (payslip) => {
    if (!payslip) {
      return '';
    }

    const rawPhone = payslip.phone || payslip.employee?.phone || '';
    if (!rawPhone) {
      return '';
    }

    const digitsOnly = String(rawPhone).replace(/\D/g, '');
    return digitsOnly;
  };

  const resolveEmployeeEmail = (payslip) => {
    if (!payslip) {
      return '';
    }

    const candidates = [
      payslip.email,
      payslip.employee?.email,
      payslip.employee?.officeEmail,
      payslip.employee?.personalEmail,
    ];

    const found = candidates.find((value) => typeof value === 'string' && value.trim());
    return found ? found.trim() : '';
  };

  const handleSendWhatsapp = async (payslip) => {
    if (!canSharePayslipWhatsapp) {
      setActionStatus({ type: 'error', message: 'You do not have permission to share payslip PDF on WhatsApp.' });
      return;
    }

    if (!payslip?.id) {
      return;
    }

    const phone = resolveEmployeePhone(payslip);
    if (!phone) {
      setActionStatus({ type: 'error', message: 'Employee mobile number is not available for WhatsApp.' });
      return;
    }

    setSharingId(payslip.id);
    setActionStatus(null);

    try {
      const { data } = await apiRequest(
        `/api/admin/payslips/${payslip.id}/whatsapp-share`,
        { method: 'POST' },
        navigate,
      );

      const sharePath = data?.url;
      if (!sharePath) {
        throw new Error('Unable to prepare payslip PDF for sharing.');
      }

      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      const normalizedPath = sharePath.startsWith('/') ? sharePath : `/${sharePath}`;
      const pdfUrl = `${baseUrl}${normalizedPath}`;
      const employeeName = payslip.name || payslip.employee?.name || 'Employee';
      const monthLabel = payslip.month || 'the selected period';
      const message = encodeURIComponent(
        `Dear ${employeeName}, your payslip for ${monthLabel} is available. Download PDF: ${pdfUrl}`,
      );

      const whatsappUrl = `https://wa.me/${phone}?text=${message}`;

      const opened = window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
      if (!opened) {
        throw new Error('Please allow pop-ups to open WhatsApp in a new tab.');
      }

      setActionStatus({ type: 'success', message: 'WhatsApp message window opened in a new tab.' });
    } catch (err) {
      setActionStatus({
        type: 'error',
        message: err.message || 'Failed to prepare WhatsApp message for this payslip.',
      });
    } finally {
      setSharingId(null);
    }
  };

  const handleSendEmail = async (payslip) => {
    if (!canSendPayslipEmail) {
      setActionStatus({ type: 'error', message: 'You do not have permission to send payslip email.' });
      return;
    }

    if (!payslip?.id) {
      return;
    }

    const email = resolveEmployeeEmail(payslip);
    if (!email) {
      setActionStatus({
        type: 'error',
        message: 'Employee email address is not available for sending.',
      });
      return;
    }

    setEmailSendingIds((prev) => [...prev, payslip.id]);
    setActionStatus(null);

    try {
      const { message } = await apiRequest(
        '/api/admin/payslips/send',
        {
          method: 'POST',
          body: { payslipIds: [payslip.id] },
        },
        navigate,
      );

      setActionStatus({
        type: 'success',
        message: message || 'Payslip email was triggered successfully.',
      });
    } catch (err) {
      setActionStatus({
        type: 'error',
        message: err.message || 'Failed to send payslip email.',
      });
    } finally {
      setEmailSendingIds((prev) => prev.filter((id) => id !== payslip.id));
    }
  };

  const loadEmployeeOptions = useCallback(async () => {
    try {
      const { data: employeeData } = await apiRequest('/api/admin/employees?status=active', {}, navigate);
      const employeeList = Array.isArray(employeeData?.employees)
        ? employeeData.employees
        : Array.isArray(employeeData)
          ? employeeData
          : [];
      setEmployees(employeeList);
    } catch (err) {
      // Filters are optional, so we log the issue for debugging and continue.
      console.error('Failed to load employee filter options', err);
    }
  }, [navigate]);

  useEffect(() => {
    loadEmployeeOptions();
  }, [loadEmployeeOptions]);

  const loadPayslips = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(limit));
      params.set('sortBy', 'paymentDate');
      params.set('sortDir', 'desc');

      if (filters.search) {
        params.set('search', filters.search.trim());
      }
      if (filters.month) {
        params.set('month', filters.month);
      }
      const trimmedYear = filters.year.trim();
      if (/^\d{4}$/.test(trimmedYear)) {
        params.set('year', trimmedYear);
      }
      if (activeCompanyId) {
        params.set('companyId', activeCompanyId);
      }
      if (filters.branchId) {
        params.set('branchId', filters.branchId);
      }
      if (filters.employeeId) {
        params.set('employeeId', filters.employeeId);
      }

      const { data, meta: responseMeta } = await apiRequest(
        `/api/admin/payslips?${params.toString()}`,
        {},
        navigate,
      );


      setPayslips(Array.isArray(data) ? data : []);
      setMeta({
        page: responseMeta?.page ?? page,
        limit: responseMeta?.limit ?? limit,
        total: responseMeta?.total ?? 0,
        totalPages: responseMeta?.totalPages ?? 0,
      });
    } catch (err) {
      setPayslips([]);
      setMeta({ page: 1, limit, total: 0, totalPages: 0 });
      setError(err.message || 'Failed to load payslips.');
    } finally {
      setLoading(false);
    }
  }, [activeCompanyId, page, limit, filters, navigate]);

  useEffect(() => {
    loadPayslips();
  }, [loadPayslips]);

  const totalPages = meta.totalPages || (meta.total > 0 ? Math.ceil(meta.total / meta.limit) : 0);

  const changePage = (newPage) => {
    if (newPage < 1) {
      return;
    }
    if (totalPages && newPage > totalPages) {
      return;
    }
    setPage(newPage);
  };

  const hasFilters = Boolean(
    filters.search.trim() || filters.month || filters.year.trim() || filters.branchId || filters.employeeId,
  );

  return (
    <DashboardLayout title="Payslip List">
      <div className="files-page employees-page container-fluid py-4">
        <div className="d-flex flex-column flex-md-row justify-content-md-end align-items-stretch align-items-md-center gap-3 mb-4">
          <button
            type="button"
            className="files-pill-button d-flex align-items-center justify-content-center"
            onClick={loadPayslips}
            disabled={loading}
          >
            <ArrowClockwise className="me-2" size={18} />
            {loading ? 'Refreshing...' : 'Refresh List'}
          </button>
          <button className="files-pill-button d-flex align-items-center justify-content-center" onClick={() => navigate('/payslip')}>
            Back
          </button>
        </div>

        <div className="files-filters shadow-sm mb-4">
          <div className="row g-3 align-items-end">
            <div className="col-lg-3 col-md-6">
              <label htmlFor="payslip-search" className="form-label text-muted small fw-semibold text-uppercase">Search Payslips</label>
              <div className="files-search-control">
                <Search className="search-icon" size={18} />
                <input
                  id="payslip-search"
                  type="text"
                  className="form-control files-input"
                  placeholder="Code, name, outlet, phone"
                  value={filters.search}
                  onChange={(event) => handleFilterChange('search', event.target.value)}
                />
              </div>
            </div>
            <div className="col-lg-2 col-md-6">
              <label htmlFor="payslip-month" className="form-label text-muted small fw-semibold text-uppercase">Month</label>
              <select id="payslip-month" className="form-select files-input" value={filters.month} onChange={(event) => handleFilterChange('month', event.target.value)}>
                <option value="">All Months</option>
                {monthOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </div>
            <div className="col-lg-2 col-md-6">
              <label htmlFor="payslip-year" className="form-label text-muted small fw-semibold text-uppercase">Year</label>
              <input
                id="payslip-year"
                type="text"
                inputMode="numeric"
                maxLength={4}
                className="form-control files-input"
                placeholder="e.g. 2026"
                value={filters.year}
                onChange={(event) => handleFilterChange('year', event.target.value.replace(/\D/g, '').slice(0, 4))}
              />
            </div>
            <div className="col-lg-3 col-md-6">
              <label htmlFor="payslip-branch" className="form-label text-muted small fw-semibold text-uppercase">Outlet</label>
              <div className="position-relative">
                <input
                  id="payslip-branch"
                  type="text"
                  className="form-control files-input"
                  placeholder="Type to search outlets"
                  value={outletSearchTerm}
                  onChange={(event) => {
                    const value = event.target.value;
                    setOutletSearchTerm(value);
                    if (filters.branchId) handleFilterChange('branchId', '');
                  }}
                  onFocus={() => outletSearchTerm.trim().length >= 2 && searchOutlets(outletSearchTerm)}
                />
                {isOutletSearching && <div className="position-absolute top-50 end-0 translate-middle-y pe-3 text-muted small">Searching...</div>}
                {outletOptions.length > 0 && (
                  <div className="dropdown-menu show w-100" style={{ maxHeight: '220px', overflowY: 'auto' }}>
                    {outletOptions.map((option) => (
                      <button key={option.value} type="button" className="dropdown-item text-wrap" onMouseDown={(event) => {
                        event.preventDefault();
                        handleFilterChange('branchId', option.value);
                        setOutletSearchTerm(option.label);
                        clearOutletOptions();
                      }}>{option.label}</button>
                    ))}
                  </div>
                )}
              </div>
              {outletSearchError && <div className="text-danger small mt-1">{outletSearchError}</div>}
            </div>
            <div className="col-lg-2 col-md-6">
              <label htmlFor="payslip-employee" className="form-label text-muted small fw-semibold text-uppercase">Employee</label>
              <select id="payslip-employee" className="form-select files-input" value={filters.employeeId} onChange={(event) => handleFilterChange('employeeId', event.target.value)}>
                <option value="">All Employees</option>
                {employees.map((employee) => (
                  <option key={employee.id} value={employee.id}>{employee.name} {employee.showId ? `(${formatEmployeeId(employee.showId)})` : ''}</option>
                ))}
              </select>
            </div>
            <div className="col-12 d-flex justify-content-end">
              <button type="button" className="employees-clear-button" onClick={resetFilters} disabled={!hasFilters}>Clear Filters</button>
            </div>
          </div>
        </div>

        <div className="card files-table-card shadow-sm">
          <div className="card-body p-0">
            {actionStatus && (
              <div className={`alert alert-${actionStatus.type === 'error' ? 'danger' : actionStatus.type === 'success' ? 'success' : 'info'} alert-dismissible fade show m-3 mb-0`} role="alert">
                {actionStatus.message}
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setActionStatus(null)} />
              </div>
            )}
            {loading ? (
              <div className="py-5 text-center"><Spinner animation="border" role="status" /></div>
            ) : error ? (
              <div className="py-5 px-3"><Alert variant="danger" className="mb-0">{error}</Alert></div>
            ) : (
              <>
                <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 p-3 border-bottom">
                  <div className="d-flex align-items-center gap-2">
                    <label className="text-muted small mb-0 fw-semibold text-uppercase" htmlFor="payslip-limit">Rows per page</label>
                    <select id="payslip-limit" className="form-select form-select-sm" style={{ maxWidth: '120px' }} value={limit} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }}>
                      {[10, 20, 50].map((size) => <option key={size} value={size}>{size}</option>)}
                    </select>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => changePage(page - 1)} disabled={page <= 1 || payslips.length === 0}>Previous</button>
                    <span className="text-muted small fw-semibold">Page {payslips.length === 0 ? 0 : page}{totalPages ? ` of ${totalPages}` : ' of 0'}</span>
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => changePage(page + 1)} disabled={payslips.length === 0 || (totalPages ? page >= totalPages : payslips.length < limit)}>Next</button>
                  </div>
                </div>
                <div className="table-responsive">
                  <table className="table table-hover mb-0 files-table align-middle">
                    <thead>
                      <tr>
                        <th>Employee Code</th>
                        <th>Employee Name</th>
                        <th>Outlet</th>
                        <th>Month</th>
                        <th>Payment Date</th>
                        <th>Net Pay</th>
                        <th>Status</th>
                        <th className="text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payslips.length === 0 ? (
                        <tr><td colSpan="8" className="text-center py-4">No payslips found for the selected filters.</td></tr>
                      ) : payslips.map((payslip) => (
                        <tr key={payslip.id}>
                          <td className="fw-semibold text-uppercase">{payslip.empCode ?? '-'}</td>
                          <td className="fw-semibold text-capitalize">{payslip.name ?? '-'}</td>
                          <td className="text-capitalize">{payslip.branchName ?? payslip.outlet?.branchName ?? '-'}</td>
                          <td>{payslip.month ?? '-'}</td>
                          <td>{formatDate(payslip.paymentDate)}</td>
                          <td className="fw-semibold">{formatCurrency(payslip.netPay)}</td>
                          <td><span className="employees-status-badge employees-status-active">{formatStatus(payslip.status)}</span></td>
                          <td className="text-center">
                            <div className="files-action-buttons d-flex justify-content-center gap-2" data-can-export-payslip-pdf={canExportPayslipPdf}>
                              {canViewPayslipPdf && (
                                <button type="button" className="btn btn-icon" aria-label="View payslip PDF" onClick={() => navigate('/payslip/view', { state: { payslip } })}><FaFilePdf size={16} /></button>
                              )}
                              {canSendPayslipEmail && (
                                <button type="button" className="btn btn-icon" aria-label="Send payslip email" onClick={() => handleSendEmail(payslip)} disabled={emailSendingIds.includes(payslip.id)}>{emailSendingIds.includes(payslip.id) ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /> : <FaEnvelope size={16} />}</button>
                              )}
                              {canSharePayslipWhatsapp && (
                                <button type="button" className="btn btn-icon" aria-label="Send payslip WhatsApp" onClick={() => handleSendWhatsapp(payslip)} disabled={sharingId === payslip.id}>{sharingId === payslip.id ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /> : <FaWhatsapp size={16} />}</button>
                              )}
                              {!canViewPayslipPdf && !canSendPayslipEmail && !canSharePayslipWhatsapp && (
                                <span className="text-muted small">No actions</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PaySlipList;
